import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FileUpload } from '@/components/ui/file-upload'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { parseError } from '@/lib/errors'
import { useQaInboxes, useSendQaEmail } from '@/models/qaEmail'
import { asFiles, attachmentLabel } from './attachments'

const FROM_STORAGE_KEY = 'qa.email.from'
const TO_STORAGE_KEY = 'qa.email.to'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSent?: (threadKey: string | null) => void
}

export function QaComposeDialog({ open, onOpenChange, onSent }: Props) {
  const [fromEmail, setFromEmail] = useState('')
  const [toEmail, setToEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const { mutateAsync, isPending } = useSendQaEmail()
  const { data: inboxes } = useQaInboxes()
  const defaultTo = inboxes?.[0]?.email ?? ''

  useEffect(() => {
    if (!open) return
    setFromEmail(localStorage.getItem(FROM_STORAGE_KEY) ?? '')
    setToEmail(localStorage.getItem(TO_STORAGE_KEY) ?? defaultTo)
    setSubject('')
    setBody('')
    setAttachments([])
  }, [open, defaultTo])

  const isValid = fromEmail.includes('@') && toEmail.includes('@') && subject.trim().length > 0

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!isValid) return
    try {
      const result = await mutateAsync({ fromEmail, toEmail, subject, body, attachments })
      localStorage.setItem(FROM_STORAGE_KEY, fromEmail)
      localStorage.setItem(TO_STORAGE_KEY, toEmail)
      toast.success(`Ingested — ${result.outcome}`)
      onSent?.(result.threadKey)
      onOpenChange(false)
    } catch (err) {
      toast.error(parseError(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Compose a QA email</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <p className="text-muted-foreground text-xs">
            Nothing is delivered. The sender resolves against real records, so a real customer address will
            act on that customer.
          </p>
          <div className="flex flex-col gap-2">
            <Label htmlFor="qa-from">From</Label>
            <Input
              id="qa-from"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="dispatch@bigtruckco.com"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="qa-to">To</Label>
            <Input
              id="qa-to"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder={defaultTo}
            />
            <div className="flex flex-wrap gap-1.5">
              {(inboxes ?? []).map((inbox) => (
                <Button
                  key={inbox.email}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setToEmail(inbox.email)}
                >
                  {inbox.name}
                </Button>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="qa-subject">Subject</Label>
            <Input id="qa-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="qa-body">Body</Label>
            <Textarea id="qa-body" rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          <div className="flex flex-col gap-2">
            <Label>Attachments</Label>
            <div className="flex items-center gap-3">
              <FileUpload multiple onChange={(files) => setAttachments(asFiles(files))}>
                <Button type="button" variant="outline">
                  Choose files
                </Button>
              </FileUpload>
              <span className="text-muted-foreground text-xs">{attachmentLabel(attachments)}</span>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="link">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!isValid} loading={isPending ? 'Sending…' : undefined}>
              Send
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
